import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Drawer, Input, List, Radio, Tag } from 'antd';
import classnames from 'classnames';
import ClassNames from 'classnames';
import './MobileSelect.less';
import Highlighter from '../../../Highlighter';
import { flatten, isEmpty, uniq } from 'lodash';
import { defaultChoiceColor } from '../../utils';
import {
  ClearOutlined,
  CloseSquareOutlined,
  DownOutlined,
  SearchOutlined,
} from '@ant-design/icons';

export default function MobileSelect({
  onChange,
  value,
  colorEnabled,
  choices,
  label,
  multiple = false,
  placeholder: propPlaceholder,
  allowClear = true,
  showSearch = true,
  showAll = false,
  autoFocus,
  defaultOpen,
  onBlur,
  id,
  size = 'large',
  bordered = true,
  group,
  disabled = false,
}) {
  const placeholder = propPlaceholder || `选择${label}`;
  const [visible, setVisible] = useState(defaultOpen || autoFocus || false);
  const [searchValue, setSearchValue] = useState('');

  const hasValue = !isEmpty(value);

  const flattenDataSource = useMemo(() => {
    const mergedChoices = (choices || []).filter((item) => item);
    if (group) {
      return flatten(mergedChoices);
    }
    return mergedChoices;
  }, [choices, group]);
  const dataSource = useMemo(() => {
    let dataSource = [...flattenDataSource];
    if (value) {
      if (multiple) {
        const unExistChoices = (value || [])
          .filter((text) => !dataSource.some((item) => (item.value || item.text) === text))
          .map((text) => ({ text, value: text, color: defaultChoiceColor }));
        dataSource = dataSource.concat(unExistChoices);
      } else if (!dataSource.some((item) => (item.value || item.text) === value)) {
        dataSource.push({ text: value, value, color: defaultChoiceColor });
      }
    }
    if (searchValue) {
      dataSource = dataSource.filter(({ text }) =>
        text.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }
    return dataSource;
  }, [flattenDataSource, searchValue, value, multiple]);

  const onDrawerClose = (options) => {
    setVisible(false);
    setSearchValue('');
    if (onBlur) {
      onBlur(options);
    }
  };

  const handleSearch = (event) => {
    event.stopPropagation();
    setSearchValue(event.target.value);
  };

  const renderSearchBar = () => {
    return (
      <Input
        className="search-input"
        onChange={(event) => handleSearch(event)}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        prefix={<SearchOutlined />}
        allowClear
        value={searchValue}
        placeholder="搜索选项"
      />
    );
  };

  const onItemClick = (choice) => {
    if (!multiple) {
      if (value === choice) {
        onChange(undefined);
      } else {
        onChange(choice);
      }
      onDrawerClose();
    } else {
      const lastValue = value || [];
      const checked = lastValue.includes(choice);
      if (checked) {
        onChange(
          lastValue.filter((item) => item !== choice),
          { doChain: !visible },
        );
      } else {
        onChange(lastValue.concat([choice]), { doChain: !visible });
      }
    }
  };

  const onPreventMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const renderItem = ({ text, value: itemValue, color, icon, children }) => {
    if (children) {
      return <List.Item className={classnames('item-group')}>{text}</List.Item>;
    }
    const key = itemValue || text;
    const selected = multiple ? (value || []).includes(key) : value === key;
    let content = <Highlighter searchValue={searchValue} text={text} />;
    if (colorEnabled) {
      content = (
        <Tag color={color || defaultChoiceColor} style={{ borderRadius: 11 }}>
          {content}
        </Tag>
      );
    }
    return (
      <List.Item
        className={classnames({ 'item-selected': selected }, { 'is-tag': colorEnabled })}
        onClick={() => onItemClick(key)}
      >
        {multiple ? <Checkbox checked={selected} /> : <Radio checked={selected} />}
        {content}
      </List.Item>
    );
  };

  const renderTitle = () => {
    if (!multiple) {
      return placeholder;
    }
    return (
      <div className="custom-header">
        <Button type="link" onClick={() => onChange([])}>
          清空
        </Button>
        <div className="title">{placeholder}</div>
        <Button type="link" onClick={onDrawerClose}>
          确定
        </Button>
      </div>
    );
  };
  const renderValue = () => {
    if (!hasValue) {
      return placeholder;
    }

    if (!colorEnabled && !multiple) {
      const choice = flattenDataSource.find((item) => (item.value || item.text) === value);
      return choice?.text || value || '';
    }
    const valueList = multiple ? value || [] : [value];
    return valueList.map((text) => {
      let color = undefined;
      const choice = flattenDataSource.find((item) => (item.value || item.text) === text);
      if (colorEnabled) {
        color = choice?.color || defaultChoiceColor;
      }
      const style = { marginRight: 3 };
      if (!colorEnabled) {
        style.borderRadius = 2;
      }
      return (
        <div className={'custom-field-select-mobile-value-wrapper-content-item'}>
          <Tag
            color={color}
            onMouseDown={onPreventMouseDown}
            closable
            onClose={() => onItemClick(text)}
            style={style}
          >
            <span>{choice?.text || text || ''}</span>
          </Tag>
        </div>
      );
    });
  };

  const renderShowAll = () => {
    if (!multiple || !showAll || isEmpty(dataSource)) {
      return null;
    }
    const selected = dataSource.every((item) => (value || []).includes(item.text));
    const indeterminate = !selected && dataSource.some((item) => (value || []).includes(item.text));
    return (
      <List.Item
        className={classnames({ 'item-selected': selected })}
        onClick={() => {
          if (selected) {
            const currentChoices = dataSource.map((item) => item.text);
            const newValue = uniq((value || []).filter((text) => !currentChoices.includes(text)));
            onChange(newValue);
          } else {
            const newValue = uniq((value || []).concat(dataSource.map((item) => item.text)));
            onChange(newValue);
          }
        }}
      >
        <Checkbox checked={selected} indeterminate={indeterminate} />
        全选
      </List.Item>
    );
  };
  const showDropdown = !hasValue || !allowClear;
  return (
    <div className={classnames('custom-field-select-mobile', { multiple, 'is-tag': colorEnabled })}>
      <div
        className={classnames('ant-input', 'custom-field-select-mobile-value-wrapper', {
          placeholder: !hasValue,
          'ant-input-borderless': !bordered,
          'ant-input-lg': size === 'large',
          'ant-input-disabled': disabled,
        })}
        onClick={() => {
          if (!disabled) {
            setVisible(true);
          }
        }}
      >
        <input
          id={id}
          onFocus={() => setVisible(true)}
          style={{ position: 'absolute', visibility: 'hidden', width: 0, height: 0 }}
        />

        <div className={'custom-field-select-mobile-value-wrapper-content'}>{renderValue()}</div>
        {hasValue && allowClear && !disabled && (
          <ClearOutlined
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onChange([], { doChain: true });
            }}
          />
        )}
        {showDropdown && <DownOutlined />}
      </div>
      <Drawer
        className={ClassNames('custom-field-select-mobile-render', { multiple })}
        visible={visible}
        title={renderTitle()}
        placement="bottom"
        height="100%"
        closable={!multiple}
        closeIcon={<CloseSquareOutlined />}
        onClose={onDrawerClose}
      >
        {showSearch && <div className="search-wrapper">{renderSearchBar()}</div>}
        {renderShowAll()}
        <List dataSource={dataSource} itemLayout="horizontal" renderItem={renderItem} />
      </Drawer>
    </div>
  );
}
